import React from 'react';

import {
	BLOCK_ACTIONS_FEATURED_EXTENSION_ITEM_RANK,
	BLOCK_ACTIONS_FEATURED_EXTENSION_SLOT_MENU_ITEM,
	BLOCK_ACTIONS_TEMPLATE_SECTION,
	MAIN_BLOCK_MENU_SECTION_RANK,
} from '@atlaskit/editor-common/block-menu';
import type { RegisterBlockMenuComponent } from '@atlaskit/editor-plugin-block-menu/blockMenuPluginType';
import { AGENT_MANAGED_EXTENSION_KEY } from '@atlaskit/editor-common/extensions';

import type { SnippetsPlugin } from '../../snippetsPluginType';

import {
	TemplateGalleryBlockMenuItem,
	TemplateGalleryBlockMenuSection,
} from './TemplateGalleryBlockMenuItem';

type Params = {
	api: Parameters<SnippetsPlugin>[0]['api'];
	onCreateSnippet?: Parameters<SnippetsPlugin>[0]['config']['onCreateSnippet'];
	onOpenTemplateGallery?: () => void;
};

const isCreateSnippetAvailable = ({ api, onCreateSnippet }: Params): boolean => {
	const preservedSelection = api?.blockControls?.sharedState.currentState()?.preservedSelection;
	return Boolean(onCreateSnippet && preservedSelection && !preservedSelection.empty);
};

const isAgentManagedBlockMenuTrigger = (api: Params['api']): boolean => {
	const { menuTriggerByNode, preservedSelection } =
		api?.blockControls?.sharedState.currentState() ?? {};
	const pos = menuTriggerByNode?.rootPos ?? menuTriggerByNode?.pos;
	if (pos === undefined || !preservedSelection) {
		return false;
	}

	return (
		preservedSelection.$from.doc.nodeAt(pos)?.attrs.extensionKey === AGENT_MANAGED_EXTENSION_KEY
	);
};

export const getTemplateGalleryBlockMenuComponents = ({
	api,
	onCreateSnippet,
	onOpenTemplateGallery,
}: Params): RegisterBlockMenuComponent[] => [
	{
		type: 'block-menu-section',
		key: BLOCK_ACTIONS_TEMPLATE_SECTION.key,
		rank: MAIN_BLOCK_MENU_SECTION_RANK[BLOCK_ACTIONS_TEMPLATE_SECTION.key],
		component: TemplateGalleryBlockMenuSection,
	},
	{
		type: 'block-menu-item',
		key: 'block-templates',
		parent: {
			type: 'block-menu-section',
			key: BLOCK_ACTIONS_TEMPLATE_SECTION.key,
			rank: BLOCK_ACTIONS_FEATURED_EXTENSION_ITEM_RANK[
				BLOCK_ACTIONS_FEATURED_EXTENSION_SLOT_MENU_ITEM.key
			],
		},
		isHidden: () =>
			isAgentManagedBlockMenuTrigger(api) ||
			(!onOpenTemplateGallery && !isCreateSnippetAvailable({ api, onCreateSnippet })),
		component: () => (
			<TemplateGalleryBlockMenuItem
				api={api}
				onCreateSnippet={onCreateSnippet}
				onOpenTemplateGallery={onOpenTemplateGallery}
			/>
		),
	},
];
